import { isThreeEM, real_id } from 'src/helpers/interface_helpers';
import { convertValueAndUnit } from 'src/helpers/utilities';
import { readDevicePhasePowers } from 'src/helpers/emeter_phase_values';

export type { EmeterPhaseMetric } from 'src/helpers/emeter_phase_values';
export { readDevicePhasePowers, readDevicePhaseValues } from 'src/helpers/emeter_phase_values';

export type PhaseHealth = {
	phase: string;
	displayName: string;
	power: number;
	label: string;
	percent: number;
	tone: 'primary' | 'success' | 'warning';
};

export type PhaseHealthSummary = {
	phases: PhaseHealth[];
	totalLabel: string;
	imbalancePercent: number;
	isBalanced: boolean;
	sourceDeviceName: string | null;
};

export const buildPhaseHealth = (
	devicesArr: { id: string; name?: string; type?: string; mode?: string }[],
	statusMap: Record<string, Record<string, unknown>>,
	channelName?: (device: { id: string }, channel: number) => string | undefined,
): PhaseHealthSummary => {
	let totals = [0, 0, 0];
	let sourceDeviceName: string | null = null;
	let sourceDevice: { id: string } | null = null;

	for (const device of [...devicesArr].sort((a, b) => String(a.id).localeCompare(String(b.id)))) {
		if (!isThreeEM(device?.type ?? '') || device?.mode === 'monophase') continue;

		const status =
			statusMap[real_id(device.id)] ?? statusMap[device.id] ?? statusMap[String(device.id)];
		const powers = readDevicePhasePowers(status);
		if (!powers) continue;
		sourceDeviceName = device.name ?? null;
		sourceDevice = device;
		totals = powers.map((power) => Math.abs(power));
		break;
	}

	if (!totals.some((value) => value > 0) && !sourceDeviceName) {
		return {
			phases: [],
			totalLabel: '—',
			imbalancePercent: 0,
			isBalanced: true,
			sourceDeviceName: null,
		};
	}

	const max = Math.max(...totals, 1);
	const min = Math.min(...totals);
	const imbalancePercent = max > 0 ? Math.round(((max - min) / max) * 100) : 0;
	const isBalanced = imbalancePercent <= 15;
	const totalPower = totals.reduce((sum, value) => sum + value, 0);
	const totalFormatted = convertValueAndUnit(totalPower, 'W');

	const phases = ['A', 'B', 'C'].map((phase, i) => {
		const { value, unit } = convertValueAndUnit(totals[i], 'W');
		const isHottest = !isBalanced && totals[i] === max && max > 0;
		return {
			phase,
			displayName: (sourceDevice && channelName?.(sourceDevice, i)) || `Phase ${phase}`,
			power: totals[i],
			label: totals[i] > 0 ? `${value} ${unit}` : '—',
			percent: Math.round((totals[i] / max) * 100),
			tone: isHottest ? 'warning' : totals[i] > 0 ? 'primary' : 'success',
		};
	}) as PhaseHealth[];

	return {
		phases,
		totalLabel: totalPower > 0 ? `${totalFormatted.value} ${totalFormatted.unit}` : '—',
		imbalancePercent,
		isBalanced,
		sourceDeviceName,
	};
};
